import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Upload,
  FileText,
  X,
  Loader2,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import useSeo from "../hooks/useSeo";

const ALLOWED_MIME = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
const MAX_BYTES = 5 * 1024 * 1024;

const STATE_CONFIG = {
  none: {
    icon: Shield,
    title: "Aún no has subido tu seguro",
    body: "Para publicar y editar vehículos necesitas un Seguro Multiplataforma vigente.",
    color: "text-neutral-600",
    bg: "bg-neutral-50",
  },
  pending: {
    icon: Clock,
    title: "En revisión",
    body: "Tu póliza está siendo revisada por nuestro equipo. Responderemos en 24-48 horas.",
    color: "text-amber-700",
    bg: "bg-amber-50",
  },
  approved_active: {
    icon: ShieldCheck,
    title: "Seguro vigente",
    body: "Estás listo para publicar y editar vehículos.",
    color: "text-emerald-700",
    bg: "bg-emerald-50",
  },
  approved_warning: {
    icon: AlertTriangle,
    title: "Tu seguro vence pronto",
    body: "Renueva tu póliza antes de la fecha de vencimiento para no pausar tus listados.",
    color: "text-amber-700",
    bg: "bg-amber-50",
  },
  expired: {
    icon: ShieldAlert,
    title: "Seguro vencido",
    body: "Pausamos tus listados. Sube tu nueva póliza para reactivar de inmediato.",
    color: "text-red-700",
    bg: "bg-red-50",
  },
  rejected: {
    icon: ShieldAlert,
    title: "Documentación rechazada",
    body: "Revisa el motivo y vuelve a subir tu póliza.",
    color: "text-red-700",
    bg: "bg-red-50",
  },
  fraud_flagged: {
    icon: ShieldAlert,
    title: "Documento marcado para revisión",
    body: "Nuestro equipo se pondrá en contacto contigo.",
    color: "text-red-700",
    bg: "bg-red-50",
  },
};

export default function HostInsurancePage() {
  const navigate = useNavigate();
  useSeo({
    title: "Seguro Multiplataforma · Rivones",
    description: "Sube tu Seguro Multiplataforma para publicar y rentar tus vehículos en Rivones.",
  });

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    owner_name: "",
    insurance_company: "",
    policy_number: "",
    expiration_date: "",
    documents: [], // [{url, original_filename, size_bytes}]
  });

  const cfg = STATE_CONFIG[state?.state] || STATE_CONFIG.none;
  const StatusIcon = cfg.icon;

  const showForm = useMemo(
    () => ["none", "expired", "rejected", "approved_warning"].includes(state?.state),
    [state],
  );

  const fetchState = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/host/insurance/me");
      setState(data);
      if (data?.record && data.state === "rejected") {
        setForm({
          owner_name: data.record.owner_name || "",
          insurance_company: data.record.insurance_company || "",
          policy_number: data.record.policy_number || "",
          expiration_date: data.record.expiration_date || "",
          documents: [],
        });
      }
    } catch (e) {
      toast.error("No pudimos cargar tu estado de seguro");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchState();
  }, []);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      if (!ALLOWED_MIME.includes(file.type)) {
        toast.error(`${file.name}: formato no admitido (solo PDF, JPG, PNG)`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name}: máximo 5 MB`);
        continue;
      }
      setUploadingIdx(file.name);
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/host/insurance/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm((f) => ({
          ...f,
          documents: [
            ...f.documents,
            {
              url: data.url,
              original_filename: data.original_filename || file.name,
              size_bytes: data.size_bytes || file.size,
              content_type: data.content_type || file.type,
            },
          ],
        }));
        toast.success(`${file.name} subido`);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : `Error al subir ${file.name}`);
      } finally {
        setUploadingIdx(null);
      }
    }
  };

  const removeDoc = (idx) => {
    setForm((f) => ({ ...f, documents: f.documents.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.owner_name.trim() || !form.insurance_company.trim() || !form.policy_number.trim() || !form.expiration_date) {
      toast.error("Completa todos los campos");
      return;
    }
    if (form.documents.length === 0) {
      toast.error("Sube al menos un documento");
      return;
    }
    setSubmitting(true);
    try {
      const isResubmit = ["rejected", "expired", "approved_warning"].includes(state?.state);
      const method = isResubmit ? "put" : "post";
      await api[method]("/host/insurance/me".replace("/me", isResubmit ? "/me" : ""), {
        owner_name: form.owner_name.trim(),
        insurance_company: form.insurance_company.trim(),
        policy_number: form.policy_number.trim(),
        expiration_date: form.expiration_date,
        document_urls: form.documents.map((d) => d.url),
      });
      toast.success("¡Póliza enviada! La revisaremos en 24-48 horas.");
      setForm({ owner_name: "", insurance_company: "", policy_number: "", expiration_date: "", documents: [] });
      await fetchState();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No pudimos enviar la póliza");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="host-insurance-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading font-bold text-2xl sm:text-3xl text-agave">
          Seguro Multiplataforma
        </h1>
        <p className="text-sm text-neutral-600 mt-1">
          RivoNes requiere que todos los anfitriones mantengan una póliza de Seguro
          Multiplataforma vigente para proteger a propietarios, conductores y a la plataforma.
        </p>
      </div>

      {/* Current status card */}
      <div
        className={`rounded-2xl border ${cfg.bg} ${state?.state === "approved_active" ? "border-emerald-200" : "border-amber-200"} p-5 mb-6`}
        data-testid="insurance-status-card"
      >
        <div className="flex items-start gap-3">
          <StatusIcon className={`w-7 h-7 ${cfg.color} flex-shrink-0 mt-0.5`} />
          <div className="flex-1">
            <h2 className={`font-heading font-bold text-lg ${cfg.color}`}>{cfg.title}</h2>
            <p className="text-sm text-neutral-700 mt-1">{cfg.body}</p>
            {state?.record && (
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <p><span className="text-neutral-500">Aseguradora:</span> <strong>{state.record.insurance_company}</strong></p>
                <p><span className="text-neutral-500">Póliza:</span> <strong>{state.record.policy_number}</strong></p>
                <p><span className="text-neutral-500">Vence:</span> <strong>{state.record.expiration_date}</strong></p>
                {typeof state.days_until_expiry === "number" && (
                  <p>
                    <span className="text-neutral-500">Días restantes:</span>{" "}
                    <strong>{state.days_until_expiry}</strong>
                  </p>
                )}
              </div>
            )}
            {state?.state === "rejected" && state?.record?.rejection_reason && (
              <div className="mt-3 p-3 rounded-lg bg-white border border-red-200 text-sm">
                <p className="font-semibold text-red-700 mb-1">Motivo del rechazo</p>
                <p className="text-neutral-700">{state.record.rejection_reason}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compliance notice */}
      <div className="rounded-2xl bg-cyan-50 border border-cyan-100 p-4 mb-6 text-sm text-cyan-900">
        <p className="leading-relaxed">
          <strong>Aviso:</strong> RivoNes requiere que todos los anfitriones mantengan una cobertura
          activa de Seguro Multiplataforma para proteger a los propietarios de vehículos,
          conductores y a la plataforma durante las rentas activas.
        </p>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="insurance-form">
          <h2 className="font-heading font-semibold text-lg text-agave">
            {state?.state === "none" ? "Sube tu póliza" : "Actualiza tu póliza"}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="owner_name" className="text-sm font-medium">
                Nombre del titular
              </Label>
              <Input
                id="owner_name"
                placeholder="Como aparece en la póliza"
                value={form.owner_name}
                onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                data-testid="insurance-owner-input"
                className="mt-1 h-11"
                required
              />
            </div>
            <div>
              <Label htmlFor="company" className="text-sm font-medium">
                Aseguradora
              </Label>
              <div className="relative mt-1">
                <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  id="company"
                  placeholder="GNP, Quálitas, HDI…"
                  value={form.insurance_company}
                  onChange={(e) => setForm({ ...form, insurance_company: e.target.value })}
                  className="pl-9 h-11"
                  data-testid="insurance-company-input"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="policy" className="text-sm font-medium">
                Número de póliza
              </Label>
              <Input
                id="policy"
                placeholder="POL-12345..."
                value={form.policy_number}
                onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                className="mt-1 h-11"
                data-testid="insurance-policy-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="expiration" className="text-sm font-medium">
                Fecha de vencimiento
              </Label>
              <div className="relative mt-1">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  id="expiration"
                  type="date"
                  value={form.expiration_date}
                  onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
                  className="pl-9 h-11"
                  data-testid="insurance-expiration-input"
                  required
                />
              </div>
            </div>
          </div>

          {/* Uploader */}
          <div>
            <Label className="text-sm font-medium">Documentos (PDF, JPG, PNG · máx. 5 MB c/u)</Label>
            <div className="mt-2 grid gap-2">
              {form.documents.map((d, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 border border-neutral-200"
                  data-testid={`insurance-doc-${i}`}
                >
                  <FileText className="w-5 h-5 text-terracotta flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{d.original_filename}</p>
                    <p className="text-xs text-neutral-500">
                      {(d.size_bytes / 1024).toFixed(0)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDoc(i)}
                    className="p-1 rounded-full hover:bg-red-50 text-red-600"
                    aria-label="Quitar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <label
              className={`mt-3 flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed border-neutral-300 hover:border-terracotta hover:bg-terracotta/5 cursor-pointer transition ${uploadingIdx ? "opacity-50 pointer-events-none" : ""}`}
              data-testid="insurance-upload-area"
            >
              {uploadingIdx ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
                  <span className="text-sm text-neutral-600">Subiendo {uploadingIdx}…</span>
                </>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-neutral-400" />
                  <span className="text-sm text-neutral-700 font-medium">
                    Toca para seleccionar archivos
                  </span>
                  <span className="text-xs text-neutral-500">o arrastra y suelta</span>
                </>
              )}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                disabled={!!uploadingIdx}
              />
            </label>
          </div>

          <Button
            type="submit"
            disabled={submitting || form.documents.length === 0}
            className="w-full h-12 bg-[#111111] hover:bg-neutral-800 text-white rounded-xl font-medium"
            data-testid="insurance-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
            Enviar para revisión
          </Button>
        </form>
      )}

      {!showForm && state?.record && (
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate("/host/dashboard")} data-testid="insurance-to-dashboard">
            Ir a mi panel
          </Button>
        </div>
      )}

      {/* Saved documents preview (for approved/pending) */}
      {state?.record?.documents?.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-semibold text-neutral-700 mb-2">Documentos en archivo</h3>
          <div className="space-y-2">
            {state.record.documents.map((d, i) => (
              <a
                key={i}
                href={d.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl bg-white border border-neutral-200 hover:border-terracotta hover:bg-terracotta/5 transition"
              >
                <FileText className="w-5 h-5 text-terracotta flex-shrink-0" />
                <p className="text-sm flex-1 truncate">{d.url.split("/").pop()}</p>
                <span className="text-xs text-neutral-500">Ver</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
